import type { Timeline } from "./timeline.js";

/**
 * The shapes a finished video can be, and where each one is for.
 *
 * Named by destination rather than by number, because "9:16" answers a
 * question nobody asked. Somebody choosing here knows where the video is
 * going, not what its ratio should be — so the ratio is the label and the
 * destination is the reason.
 *
 * The dimensions are what each platform actually wants: 1080 on the short
 * edge, which is the size every one of them re-encodes to anyway. A taller
 * master gains nothing once it has been through TikTok.
 */
export const aspectRatios = [
  { key: "16:9", width: 1920, height: 1080, use: "YouTube, websites, Slack" },
  { key: "9:16", width: 1080, height: 1920, use: "TikTok, Reels, Shorts" },
  { key: "4:5", width: 1080, height: 1350, use: "LinkedIn and Facebook" },
  { key: "1:1", width: 1080, height: 1080, use: "Instagram posts" },
] as const;

export type AspectRatio = (typeof aspectRatios)[number];
export type AspectKey = AspectRatio["key"];

/**
 * Which shape this timeline is, or null for one that is neither.
 *
 * Compared as a ratio rather than by exact dimensions, so a project rendered
 * at 1280×720 is still 16:9 and a 4K one still matches. Null is a real answer
 * — a timeline can carry any size within the schema's bounds, and claiming it
 * is 1:1 because that is nearest would light the wrong tile.
 */
export function aspectOf(timeline: { width: number; height: number }): AspectKey | null {
  const ratio = timeline.width / timeline.height;
  const match = aspectRatios.find((option) => Math.abs(option.width / option.height - ratio) < 0.01);
  return match?.key ?? null;
}

/**
 * The export sizes for a timeline, at its own shape.
 *
 * Generated rather than listed, which is the whole reason this is here: the
 * export screen used to offer three fixed 16:9 sizes, so a vertical project
 * would have been rendered wide — reflowing every card and letterboxing the
 * recording inside a frame it was never composed for.
 *
 * Two of them, at the shape the timeline already is: full size, and a smaller
 * one for email and quick shares. A larger one is `comingSoon` on 16:9 only,
 * because that is the only shape anybody has asked to hand to a projector.
 */
export function exportSizesFor(timeline: Pick<Timeline, "width" | "height">): Array<{
  label: string;
  width: number;
  height: number;
  use: string;
}> {
  const ratio = timeline.width / timeline.height;
  const short = Math.min(timeline.width, timeline.height);

  // Even numbers throughout: h264 wants both dimensions divisible by two, and
  // a 4:5 frame at 720 on the short edge is 900 exactly, which is not a given
  // for every ratio somebody might land on.
  const at = (shortEdge: number) => {
    const wide = ratio >= 1;
    const long = Math.round((wide ? shortEdge * ratio : shortEdge / ratio) / 2) * 2;
    return wide ? { width: long, height: shortEdge } : { width: shortEdge, height: long };
  };

  const full = at(short >= 1080 ? 1080 : short);
  const small = at(720);

  return [
    { label: `${full.width}×${full.height}`, ...full, use: "Websites, docs and social" },
    { label: `${small.width}×${small.height}`, ...small, use: "Email and quick shares" },
  ];
}
